import dayjs from 'dayjs';
import { chain, isEmpty } from 'lodash';
import { useContext, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { AuthDataProvider } from 'src/components/authProvider';
import {
  DEFAULT_POSITION_ALL,
  LISTVIEW_MAX_ROWS_PER_PAGE,
} from 'src/constants';
import {
  useGetAllDayparts,
  useGetDaypartsByLocationGroup,
  useGetLocation,
  useGetPaginatedDefaultAssemblies,
  useGetProjectConfig,
  useParamQuery,
} from 'src/hooks';
import { checkIsDefaultLocationGroup } from 'src/hooks/api/locations/helper';
import { useExcluseScheduleLocation } from 'src/hooks/api/schedules/scheduleLocation/useExcluseScheduleLocation';
import { useGetPaginatedSchedules } from 'src/hooks/api/schedules/useGetAllSchedules';
import { useApiErrorNavigator } from 'src/hooks/navigation';
import { IDaypart } from 'src/interfaces';
import { formatDate } from 'src/utils';
import { formatTimestamp } from 'src/utils/date';

import { VIEW_OPTIONS } from '../../scheduleLocationGroupPage/modules/LocationSchedulePageTable/LocationSchedulePageTable.interface';

export const useLocationSchedulePage = () => {
  const { locationCode = '' } = useParams();
  const [rowsPerPage, setRowsPerPage] = useState(LISTVIEW_MAX_ROWS_PER_PAGE);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({
    daypartId: 1,
    position: DEFAULT_POSITION_ALL,
  });
  const [selectedOption, setSelectedOption] = useState<string>(
    VIEW_OPTIONS.SCHEDULE_COMPONENT,
  );
  const { data: daypartOptions, isLoading: isLoadingDayparts } =
    useGetAllDayparts();
  const { getParamsValue, onChangeParams } = useParamQuery();
  const { userRole } = useContext(AuthDataProvider);
  const { data: location, error } = useGetLocation(locationCode, userRole);
  const locationGroupId = getParamsValue('groupID').join();

  const isDefaultLocationGroup = checkIsDefaultLocationGroup(location);
  const isDefaultAssembly = selectedOption === VIEW_OPTIONS.DEFAULT_ASSEMBLY;
  const [sortKey, setSortKey] = useState<string>('modified_at');
  const [sortDirection, setSortDirection] = useState<string>('descending');
  const [sortSelected, setSortSelected] = useState(false);

  const sortParam = `${sortDirection === 'descending' ? '-' : ''}${sortKey}`;
  const formMethods = useForm({
    values: {
      location: location,
    },
  });
  const {
    data: {
      defaultAssemblyData = [],
      totalPages = 1,
      totalCount: totalCountDefault = 1,
    } = {},
    isLoading: isLoadingScheduleDefaultAssemblies,
    refetch: refetchSchedules,
  } = useGetPaginatedDefaultAssemblies(
    [
      `location_group_id=${locationGroupId}`,
      `sort=${sortParam}`,
      `page=${page}`,
      `daypart_id=${filters.daypartId}`,
      `pageSize=${rowsPerPage}`,
      `currentDate=${dayjs().format('YYYY-MM-DD')}`,
    ],
    Boolean(locationGroupId) && isDefaultAssembly,
  );
  const { data: projectConfig } = useGetProjectConfig();
  const {
    data: {
      schedulesData = [],
      totalSchedulesPages = 1,
      totalCount: totalCountSchedule = 1,
    } = {},
    isLoading: isLoadingSchedules,
  } = useGetPaginatedSchedules(
    [
      `location_group_id=${locationGroupId}`,
      `location_code=${locationCode}`,
      `sort=${sortParam}`,
      `page=${page}`,
      `daypart_id=${filters.daypartId}`,
      `position=${filters.position}`,
      `pageSize=${rowsPerPage}`,
    ],
    Boolean(locationGroupId) && !isDefaultAssembly,
  );
  const {
    data: activeDaypartOptions = [],
    isLoading: isLoadingActiveDayparts,
  } = useGetDaypartsByLocationGroup(
    [`location_group_id=${locationGroupId}`, 'valid_to.null=true'],
    Boolean(locationGroupId),
  );
  useEffect(() => {
    setPage(1);
    setFilters({
      daypartId: 1,
      position: DEFAULT_POSITION_ALL,
    });
  }, [selectedOption]);

  const excludeScheduleLocation = useExcluseScheduleLocation();

  const locationGroup = (
    formMethods.getValues().location?.locationGroups ?? []
  ).find((group) => group.id?.toString() === locationGroupId);

  useApiErrorNavigator(error);

  useEffect(() => {
    const locationGroups =
      formMethods.getValues().location?.locationGroups ?? [];
    const locationGroupId = getParamsValue('groupID').join();

    if (!isEmpty(locationGroups) && !locationGroupId) {
      const firstLocationGroupId = locationGroups[0]?.id || '';

      onChangeParams('groupID', firstLocationGroupId as string, true);
    }
  }, [formMethods, getParamsValue, onChangeParams]);

  const onChangedLocationGroupVal = (value: string) => {
    onChangeParams('groupID', value, true);
  };

  const onChangeLocationGroup = (
    event: React.SyntheticEvent,
    newValue: string,
  ) => {
    event.stopPropagation();
    onChangedLocationGroupVal(newValue);
  };
  const individualLocationSchedules = chain(defaultAssemblyData)
    .filter(
      (assembly) =>
        !assembly.locationCode || assembly.locationCode === locationCode,
    )
    .value();
  const handleDaypartChange = (newDaypartId: number) => {
    setPage(1);
    setFilters((prev) => ({ ...prev, daypartId: newDaypartId }));
  };
  const handlePositionChange = (newPositionId: string) => {
    setPage(1);
    setFilters((prev) => ({ ...prev, position: newPositionId }));
  };
  const handleRowsPerPageChange = (newValue: number) => {
    setRowsPerPage(newValue);
    setPage(1); // reset to first page on page size change
  };
  const formattedSchedulesData = (schedulesData || []).map((item) => ({
    ...item,
    start_date: formatDate(item.start_date as string),
    end_date: formatDate(item.end_date as string),
    modified_at: formatTimestamp(item?.modified_at || '') || '',
    latest_override_date: item.latest_override_date
      ? formatTimestamp(item.latest_override_date as string)
      : '',
    scheduleItems: (item.scheduleItems || []).map((scheduleItem) => ({
      ...scheduleItem,
      start_date: formatDate(scheduleItem.start_date),
      end_date: formatDate(scheduleItem.end_date as string),
      modified_at: '',
      overrides: (scheduleItem.overrides || []).map((override) => ({
        ...override,
        start_date: formatDate(override.start_date),
        end_date: formatDate(override.end_date as string),
        created_at: override.created_at
          ? formatTimestamp(override.created_at as string)
          : '',
      })),
    })),
  }));
  const shouldShowListview = {
    daypartDisabled:
      !isLoadingActiveDayparts && activeDaypartOptions.length === 0,
    noDefaultAssembly:
      !isLoadingActiveDayparts &&
      !isLoadingScheduleDefaultAssemblies &&
      isDefaultAssembly &&
      activeDaypartOptions.length > 0 &&
      defaultAssemblyData.length === 0,
    noScheduleComponent:
      !isLoadingActiveDayparts &&
      !isLoadingSchedules &&
      !isDefaultAssembly &&
      activeDaypartOptions.length > 0 &&
      schedulesData.length === 0,
  };

  return {
    formMethods,
    individualLocationScheduleData: individualLocationSchedules,
    formattedSchedulesData,
    onChangeLocationGroup,
    isLoadingScheduleDefaultAssemblies,
    isLoadingSchedules,
    refetchSchedules,
    locationGroup,
    isDefaultLocationGroup,
    page,
    totalPages: totalPages,
    totalSchedulesPages,
    onPageChange: (_: unknown, newPage: number) => setPage(newPage),
    selectedDaypartId: filters.daypartId,
    selectedPositionId: filters.position,
    daypartOptions: daypartOptions as IDaypart[],
    isLoadingDayparts: isLoadingDayparts,
    onDaypartChange: handleDaypartChange,
    onPositionChange: handlePositionChange,
    selectedOption: selectedOption,
    onSelectedOptionChange: (newSelectedOption: string) => {
      setSelectedOption(newSelectedOption);
      setSortSelected(false);
    },
    projectConfig: projectConfig,
    isDefaultAssembly: isDefaultAssembly,
    sortKey,
    setSortKey,
    sortDirection,
    setSortDirection,
    setPage,
    sortSelected,
    setSortSelected,
    totalCountDefault,
    totalCountSchedule,
    rowsPerPage,
    handleRowsPerPageChange,
    excludeScheduleLocation,
    shouldShowListview,
  };
};
